import { includes, flatten, get, map, chain } from 'lodash';
import { ValueGetterParams } from 'ag-grid-community';
import { Metadata } from '@geniuspos/xenial-data-contracts/types/dm-entities/metadata';

import {
  CELL_FILTER,
  ENTITY_TYPES,
  ISiteTag,
  EntityId,
  IXVIListColumn,
  CELL_RENDERER,
  IBaseEntityType,
} from '../types';
import { stringCaseInsensitiveComparator } from '../common/string-case-insensitive.comparator';
import { COMMON_WARN_ICON_COLUMN } from '../constants/common-warn-grid-setting';
import { I18n } from '@app/i18n/strings-keys';

const getSiteSelectorColumnsDefinition = (
  entityIdsForMetadatas: EntityId[],
): IXVIListColumn[] => [
  {
    path: 'hierarchy',
    title: 'Group',
    defaultView: true,
    enableSorting: false,
    minWidth: 170,
    cellRenderer: CELL_RENDERER.ag_group_cell_renderer,
    hide: true,
    showRowGroup: true,
    cellClass: 'cell-hierarchy',
  },
  {
    ...COMMON_WARN_ICON_COLUMN,
    path: 'siteInUse',
    cellRendererParams: {
      iconClass: 'xvi-grid-warn-icon',
      icon: 'icon-warning',
    },
    defaultView: true,
  },
  {
    title: I18n.Sites.SiteId,
    path: 'store_number',
    defaultView: true,
    sortable: true,
    comparator: stringCaseInsensitiveComparator,
    filterParams: {
      quickFilter: true,
    },
  },
  {
    title: I18n.Sites.SiteName,
    path: 'name',
    defaultView: true,
    comparator: stringCaseInsensitiveComparator,
    sortable: true,
    filterParams: {
      quickFilter: true,
    },
  },
  {
    title: I18n.EntityNames.Tags,
    path: 'metadata',
    filterType: CELL_FILTER.entity,
    filterParams: {
      quickFilter: true,
      entity: ENTITY_TYPES.TAG,
      entitySelector: (item: ISiteTag): boolean =>
        includes(entityIdsForMetadatas, item.entity_id),
      default: {
        type: 'containsAny',
      },
      filterOptions: ['containsAny'],
      filterItemSourceQuery: {
        filter: `metadata_type eq 'tag'`,
        select: ['entity_id', 'name', '_id'],
      },
      filterValueGetter: (data): EntityId[] =>
        flatten(map(data.metadata, 'entity_id')),
    },
    valueGetter: (params: ValueGetterParams): string => {
      const metadata: Metadata[] = get(params, 'data.metadata', []);

      return chain(metadata)
        .filter({ metadata_type: 'tag' })
        .map('name')
        .join(', ')
        .value();
    },
    getQuickFilterTextForTreeView: (params: IBaseEntityType): string => {
      return params?.metadata
        ? chain(params.metadata)
            .filter({ metadata_type: 'tag' })
            .map('name')
            .join(', ')
            .value()
        : '';
    },
    defaultView: true,
    sortable: true,
  },
];

export { getSiteSelectorColumnsDefinition };
